import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { Tag } from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { StatsBar, type StatItem } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { EMPTY_CELL } from 'utils/empty-cell';
import { parseUTCdateTime } from 'utils/dates';
import { useSidekiqQueueAssignments } from './hooks';
import type { SidekiqQueueAssignment, SidekiqRouting } from './types';
import styles from './page.module.scss';

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: false,
  columnResizing: true,
  columnReordering: true,
  columnPinning: true,
  columnVisibility: true,
} as const;

type RoutingTag = {
  type: 'gray' | 'purple' | 'teal' | 'cyan' | 'red';
  label: string;
};

// How each routing resolution renders: a distinct tag so an error or a re-routed job is never
// shown as a plain "Dynamic" lane.
const ROUTING_TAG: Record<SidekiqRouting, RoutingTag> = {
  static: { type: 'gray', label: 'Static' },
  mixed: { type: 'purple', label: 'Mixed' },
  data_driven: { type: 'teal', label: 'Dynamic' },
  call_site: { type: 'cyan', label: 'Call-site' },
  unresolved: { type: 'red', label: 'Unresolved' },
  inactive: { type: 'gray', label: 'Inactive' },
};

const columnHelper = createColumnHelper<SidekiqQueueAssignment>();

const columns = [
  columnHelper.accessor('jobClass', { header: 'Job Class', size: 360 }),
  columnHelper.accessor('queue', {
    header: 'Queue',
    size: 180,
    cell: (info) => info.getValue() || EMPTY_CELL,
  }),
  columnHelper.accessor('bulkQueue', {
    header: 'Bulk Queue',
    size: 150,
    cell: (info) => info.getValue() || EMPTY_CELL,
  }),
  // Accessor returns the visible label so global search and sort follow what's shown ("Dynamic",
  // "Call-site") rather than the raw value ("data_driven", "call_site").
  columnHelper.accessor(
    (row) => ROUTING_TAG[row.routing]?.label ?? row.routing,
    {
      id: 'routing',
      header: 'Routing',
      size: 120,
      cell: (info) => {
        const tag =
          ROUTING_TAG[info.row.original.routing] ?? ROUTING_TAG.static;
        return (
          <Tag type={tag.type} size="sm">
            {tag.label}
          </Tag>
        );
      },
    }
  ),
  columnHelper.accessor('note', {
    header: 'Note',
    size: 380,
    enableSorting: false,
    cell: (info) => (
      <span title={info.getValue() ?? ''}>{info.getValue() || EMPTY_CELL}</span>
    ),
  }),
];

/**
 * Admin page listing every ActiveJob class the resolver can enumerate and the Sidekiq queue it is
 * enqueued onto, resolved the way the app routes a job at enqueue time so it reflects the live
 * SIDEKIQ_QUEUE_OVERRIDES re-tiering.
 *
 * @return {JSX.Element} The page.
 */
export function SidekiqQueueAssignmentsPage() {
  const { rows, stats, generatedAt, isLoading, error } =
    useSidekiqQueueAssignments();

  // One tile per routing so the numbers match the table's tags; Inactive/Unresolved only when present.
  const statItems: StatItem[] = stats
    ? [
        { label: 'Total Jobs', value: stats.totalJobs, tagType: 'blue' },
        { label: 'Static', value: stats.staticJobs, tagType: 'gray' },
        { label: 'Mixed', value: stats.mixedJobs, tagType: 'purple' },
        { label: 'Dynamic', value: stats.dataDrivenJobs, tagType: 'teal' },
        { label: 'Call-site', value: stats.callSiteJobs, tagType: 'cyan' },
        { label: 'Queues', value: stats.queueCount, tagType: 'blue' },
        ...(stats.inactiveJobs > 0
          ? [
              {
                label: 'Inactive',
                value: stats.inactiveJobs,
                tagType: 'gray' as const,
              },
            ]
          : []),
        ...(stats.unresolvedJobs > 0
          ? [
              {
                label: 'Unresolved',
                value: stats.unresolvedJobs,
                tagType: 'red' as const,
              },
            ]
          : []),
      ]
    : [];

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Sidekiq Queue Assignments"
        actions={null}
      />

      <div className={styles.sectionHeader}>
        <p>
          Every ActiveJob class the resolver can enumerate and the Sidekiq queue
          it is enqueued onto, resolved the same way the app routes a job at
          enqueue time so it reflects the live{' '}
          <code>SIDEKIQ_QUEUE_OVERRIDES</code> and{' '}
          <code>SIDEKIQ_BULK_QUEUE_OVERRIDES</code> re-tiering, not just the
          queue declared in code. Framework jobs and raw Sidekiq workers are not
          listed.
        </p>
        <p className={styles.sectionDescription}>
          <strong>Static</strong> enqueues onto the queue shown (including any
          live override); <strong>Mixed</strong> declares a lane but some call
          sites re-route it; <strong>Dynamic</strong> is a data-driven{' '}
          <code>tiered_queue</code> block; <strong>Call-site</strong> has its
          queue decided at the enqueue site (<code>.set(queue:)</code>);{' '}
          <strong>Inactive</strong> is an abstract/dead class never enqueued;{' '}
          <strong>Unresolved</strong> failed to resolve. The{' '}
          <strong>Note</strong> says where the queue is decided and flags active
          overrides; <strong>Bulk Queue</strong> is the second lane of a
          dual-mode job.
          {generatedAt && (
            <span className={styles.muted}>
              {' '}
              Resolved {parseUTCdateTime(generatedAt, true)} UTC.
            </span>
          )}
        </p>
      </div>

      {stats && <StatsBar stats={statItems} />}

      <DataTable
        data={rows}
        columns={columns as ColumnDef<SidekiqQueueAssignment>[]}
        getRowId={(row) => row.jobClass}
        isLoading={isLoading}
        error={error}
        config={TABLE_CONFIG}
      />
    </>
  );
}
